import { useState } from "react";
import { Link, Navigate, useLocation as useRouterLocation } from "react-router-dom";
import { useLang } from "../lib/useLang";
import { formatKWD } from "../lib/format";
import { formatSchedule } from "../lib/schedule";
import type { PaymentMethod } from "../lib/types";

interface ConfirmState {
  orderId: string;
  name: string;
  paymentMethod: PaymentMethod;
  subtotal: number;
  discount?: number;
  deliveryFee: number;
  total: number;
  scheduledFor?: number | null;
}

export default function ConfirmationPage() {
  const { t, lang } = useLang();
  const routerLoc = useRouterLocation();
  const state = routerLoc.state as ConfirmState | null;
  const [paid, setPaid] = useState(false);

  if (!state?.orderId) return <Navigate to="/" replace />;

  const isCod = state.paymentMethod === "cod";

  return (
    <div className="mx-auto max-w-md space-y-5 py-6 text-center">
      <div className="text-6xl">✅</div>
      <h1 className="text-2xl font-extrabold text-gray-900">{t("confirmation.title")}</h1>
      <p className="text-black/60">{t("confirmation.thanks", { name: state.name })}</p>

      <div className="card space-y-2 p-5 text-start">
        <div className="flex items-center justify-between">
          <span className="text-black/55">{t("confirmation.orderNo")}</span>
          <span className="text-lg font-extrabold text-brand" dir="ltr">
            #{state.orderId}
          </span>
        </div>
        {state.discount != null && state.discount > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-black/55">{t("cart.discount")}</span>
            <span className="font-semibold text-emerald-600">
              − {formatKWD(state.discount, lang)}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between text-sm">
          <span className="text-black/55">{t("cart.total")}</span>
          <span className="font-bold">{formatKWD(state.total, lang)}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-black/55">{t("checkout.payment")}</span>
          <span className="font-semibold">{t(`checkout.${state.paymentMethod}`)}</span>
        </div>
        {state.scheduledFor != null && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-black/55">🗓️ {t("schedule.title")}</span>
            <span className="font-semibold">{formatSchedule(state.scheduledFor, lang)}</span>
          </div>
        )}
      </div>

      {isCod ? (
        <p className="rounded-xl bg-cream p-3 text-sm text-black/70">
          💵 {t("confirmation.codNote")}
        </p>
      ) : paid ? (
        <p className="rounded-xl bg-accent/10 p-3 text-sm font-semibold text-accent-dark">
          ✔ {t("admin.pay.paid")}
        </p>
      ) : (
        <button className="btn-primary w-full" onClick={() => setPaid(true)}>
          {t("confirmation.payNow")} · {formatKWD(state.total, lang)}
        </button>
      )}

      <p className="text-sm text-black/50">{t("confirmation.statusNote")}</p>

      <div className="flex gap-2">
        <Link to={`/track/${state.orderId}`} className="btn-primary flex-1">
          🛵 {t("track.find")}
        </Link>
        <Link to="/" className="btn-outline flex-1">
          {t("confirmation.newOrder")}
        </Link>
      </div>
    </div>
  );
}
